import { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    IconButton,
    Chip,
    Divider,
    Card,
    CardContent,
    Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { CARDIO_TYPEN, INTENSITAETEN } from './cardioConstants';

function SessionKarte({ session, onEdit, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const typInfo = CARDIO_TYPEN.find(t => t.value === session.cardio_typ);
    const intensitaetInfo = INTENSITAETEN.find(i => i.value === session.intensitaet);

    const formatDatum = (d) =>
        d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

    return (
        <Card sx={{
            mb: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '16px',
        }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={expanded ? 2 : 0}>
                    <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                        <Typography sx={{ fontSize: '1.6rem', lineHeight: 1 }}>
                            {typInfo?.emoji || '⚡'}
                        </Typography>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 700 }}>
                                {typInfo?.label || session.cardio_typ}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {formatDatum(session.datum)}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Schnell-Stats immer sichtbar */}
                    <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', mr: 1 }}>
                        <Chip
                            icon={<DirectionsRunIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label={`${session.dauer_minuten} min`}
                            size="small"
                            variant="outlined"
                            color="primary"
                        />
                        {session.durchschnitts_bpm && (
                            <Chip
                                icon={<FavoriteIcon sx={{ fontSize: '0.85rem !important' }} />}
                                label={`${session.durchschnitts_bpm} bpm`}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: '#f87171', color: '#f87171' }}
                            />
                        )}
                        {session.kalorien && (
                            <Chip
                                icon={<LocalFireDepartmentIcon sx={{ fontSize: '0.85rem !important' }} />}
                                label={`${session.kalorien} kcal`}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: '#fb923c', color: '#fb923c' }}
                            />
                        )}
                        {intensitaetInfo && (
                            <Chip
                                label={intensitaetInfo.label}
                                size="small"
                                sx={{
                                    borderColor: intensitaetInfo.color,
                                    color: intensitaetInfo.color,
                                    border: '1px solid',
                                    bgcolor: 'transparent',
                                    fontWeight: 600,
                                }}
                            />
                        )}
                    </Box>

                    <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
                        {!confirmDelete ? (
                            <IconButton onClick={() => setConfirmDelete(true)} size="small" color="error">
                                <DeleteIcon />
                            </IconButton>
                        ) : (
                            <Box display="flex" gap={0.5}>
                                <IconButton onClick={() => setConfirmDelete(false)} size="small" color="inherit">
                                    <CloseIcon />
                                </IconButton>
                                <IconButton onClick={() => { setConfirmDelete(false); onDelete(); }} size="small" color="error">
                                    <CheckIcon />
                                </IconButton>
                            </Box>
                        )}
                        <IconButton onClick={onEdit} size="small" color="warning"><EditIcon /></IconButton>
                        <IconButton onClick={() => setExpanded(e => !e)} size="small">
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={expanded}>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                        {session.distanz_km && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary" display="block">Distanz</Typography>
                                <Typography variant="body1" fontWeight={600}>{session.distanz_km} km</Typography>
                            </Grid>
                        )}
                        {session.durchschnitts_bpm && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary" display="block">Ø Herzfrequenz</Typography>
                                <Typography variant="body1" fontWeight={600}>{session.durchschnitts_bpm} bpm</Typography>
                            </Grid>
                        )}
                        {session.max_bpm && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary" display="block">Max. HF</Typography>
                                <Typography variant="body1" fontWeight={600}>{session.max_bpm} bpm</Typography>
                            </Grid>
                        )}
                        {session.kalorien && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary" display="block">Kalorien</Typography>
                                <Typography variant="body1" fontWeight={600}>{session.kalorien} kcal</Typography>
                            </Grid>
                        )}
                        {session.notizen && (
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Notizen</Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                    {session.notizen}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Collapse>
            </CardContent>
        </Card>
    );
}

export default SessionKarte;
